import React, { useCallback, useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import schedulingService from '../../../services/scheduling.service';
import { RescheduleHistoryItem } from '../../../types/scheduling';
import { serviceFailureTypeMissing } from '../../../constants/serviceFailureTypes';
import RescheduleEditModal from './RescheduleEditModal';

export interface ReschedulesPanelProps {
  /** Pro number for the order. List/refetch is gated on this being defined. */
  pronumber: number | undefined;
  /** Order's billto — passed through so the modal can gate THD-only fields. */
  billTo?: number | null;
  /** Notifies the parent of the live count for the tab badge. */
  onCountChange?: (count: number) => void;
  /** Bumped by the parent to force a refetch (e.g. after a Reschedule
   *  Requested ticket triggers a server-side insert). */
  refreshNonce?: number;
}

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; reschedule: RescheduleHistoryItem };

/** Format a YYYY-MM-DD-ish value as m/d (or m/d/yy if not current year). */
const formatShortDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return '—';
  const date = new Date(y, m - 1, d);
  const currentYear = new Date().getFullYear();
  const mm = String(date.getMonth() + 1);
  const dd = String(date.getDate());
  if (date.getFullYear() !== currentYear) {
    return `${mm}/${dd}/${String(date.getFullYear()).slice(-2)}`;
  }
  return `${mm}/${dd}`;
};

/**
 * Reschedules list panel for the Pro 360 OrderFormView "Service Failures"
 * sub-tab. Mirrors TroubleTicketsPanel: small clickable cards + a "+ New"
 * button, all click into the shared RescheduleEditModal (which switches
 * between create and edit mode based on whether a row is passed).
 */
const ReschedulesPanel: React.FC<ReschedulesPanelProps> = ({
  pronumber,
  billTo,
  onCountChange,
  refreshNonce = 0,
}) => {
  const [rows, setRows] = useState<RescheduleHistoryItem[]>([]);
  // Start in the loading state when there's a pro to fetch, so the spinner
  // shows immediately on first mount instead of the "No Service Failures" empty
  // state flashing for a frame before the fetch effect runs.
  const [loading, setLoading] = useState<boolean>(() => pronumber != null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });

  const fetchRows = useCallback(async () => {
    if (!pronumber) {
      setRows([]);
      onCountChange?.(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await schedulingService.getReschedulesForOrder(pronumber);
      setRows(list);
      onCountChange?.(list.length);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch reschedules:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load reschedules');
    } finally {
      setLoading(false);
    }
  }, [pronumber, onCountChange]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows, refreshNonce]);

  const handleSaved = useCallback(() => {
    void fetchRows();
  }, [fetchRows]);

  const closeModal = useCallback(() => setModal({ kind: 'closed' }), []);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 text-rose-600" />
          <span className="text-sm font-semibold text-body">Service Failures</span>
          {rows.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
              {rows.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'create' })}
          disabled={!pronumber}
          className="px-2 py-0.5 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded hover:bg-rose-100 dark:text-rose-300 dark:bg-rose-900/30 dark:border-rose-700 dark:hover:bg-rose-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="New reschedule"
        >
          + New
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-4" role="status" aria-label="Loading">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rose-600" />
            <span className="text-sm text-gray-400 dark:text-gray-500">Loading…</span>
          </div>
        ) : error ? (
          <div className="py-4 text-center text-xs text-red-600 dark:text-red-300">{error}</div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center py-4 text-sm text-gray-400 dark:text-gray-500">
            No Service Failures
          </div>
        ) : (
          <div className="space-y-1.5">
            {rows.map((row) => {
              const reasonText = row.reason || 'No Reason';
              // A controllable reason with no type is also incomplete — it can
              // never count against OTD until someone says what failed.
              const isIncomplete =
                !row.reason ||
                !row.servicefailuredate ||
                serviceFailureTypeMissing(row.reason, row.servicefailuretype);
              const hasMeta = Boolean(row.responsibledriver || row.comments);
              return (
                <div
                  key={row.recno}
                  role="button"
                  tabIndex={0}
                  onClick={() => setModal({ kind: 'edit', reschedule: row })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setModal({ kind: 'edit', reschedule: row });
                    }
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 bg-panel hover:bg-highlight dark:border-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    {/* Top row: category (reason) on the left, service-failure
                        date on the right. */}
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-medium text-body truncate"
                        title={reasonText}
                      >
                        {reasonText}
                      </span>
                      {row.servicefailuretype && (
                        <span
                          className="flex-shrink-0 text-[0.625rem] text-muted truncate"
                          title="Service failure type"
                        >
                          {row.servicefailuretype}
                        </span>
                      )}
                      {isIncomplete && (
                        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          Incomplete
                        </span>
                      )}
                      <span className="ml-auto flex-shrink-0 text-[0.6875rem] font-medium text-gray-600 dark:text-gray-300">
                        {formatShortDate(row.servicefailuredate)}
                      </span>
                    </div>
                    {/* Metadata line: responsible driver (if any) + comments. */}
                    {hasMeta && (
                      <div className="flex items-center gap-1.5 mt-0.5 text-[0.625rem] text-muted min-w-0">
                        {row.responsibledriver && (
                          <span className="flex-shrink-0 font-medium text-gray-600 dark:text-gray-300">
                            Driver {row.responsibledriver}
                          </span>
                        )}
                        {row.responsibledriver && row.comments && (
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                        )}
                        {row.comments && (
                          <span className="truncate" title={row.comments}>
                            {row.comments}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal.kind !== 'closed' && pronumber !== undefined && (
        <RescheduleEditModal
          pronumber={pronumber}
          billTo={billTo ?? undefined}
          reschedule={modal.kind === 'edit' ? modal.reschedule : undefined}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default ReschedulesPanel;